/**
 * Scheduler ProのEvent操作のヘルパー関数
 */

import C from "../Constants";

const PHANTOM_GROUP_KEY = "phantom_group_key";
const ENTERPRISE_MANNING_VESSEL_CREW_ID = "enterprise_manning_vessel_crew_id";
/**
 * Phantom Group Keyを生成する関数
 * @returns {string} 生成されたPhantom Group Key
 */
const genPhantomGroupKey = () => {
  return `gk_${window.crypto.randomUUID()}`;
};

/**
 * phantomGroupKeyを設定するヘルパー関数
 * @param {*} event schedulerのイベントオブジェクト
 * @param {*} phantomGroupKey 設定するPhantom Group Key
 * @returns {void}
 */
const setPhantomGroupKey = (event, phantomGroupKey) => {
  event.set(PHANTOM_GROUP_KEY, phantomGroupKey);
};

/**
 * 乗船期間IDを設定するヘルパー関数
 * @param {*} event schedulerのイベントオブジェクト
 * @param {*} enterpriseManningVesselCrewId 乗船期間ID
 */
const setEnterpriseManningVesselCrewId = (
  event,
  enterpriseManningVesselCrewId
) => {
  event.set(ENTERPRISE_MANNING_VESSEL_CREW_ID, enterpriseManningVesselCrewId);
};

/**
 * 乗船期間IDを変更するヘルパー関数
 *
 * 乗船期間IDを変更して、イベントに置換フラグを立てる
 * @param {*} event schedulerのイベントオブジェクト
 * @param {*} enterpriseManningVesselCrewId 乗船期間ID
 */
const changeEnterpriseManningVesselCrewId = (
  event,
  enterpriseManningVesselCrewId
) => {
  // 乗船期間IDを変更する
  setEnterpriseManningVesselCrewId(event, enterpriseManningVesselCrewId);
  softReplace(event);
};

/**
 * 削除フラグを立てるヘルパー関数
 * @param {*} event schedulerのイベントオブジェクト
 */
const softDelete = event => {
  // 内部用削除フラグを立てる
  event.set("innerDelete", true);
};

/**
 * 内部用コピーフラグを立てるヘルパー関数
 * @param {*} event schedulerのイベントオブジェクト
 */
const softReplace = event => {
  // 内部用コピーフラグを立てる
  event.set("innerReplace", true);
  softDelete(event);
};

/**
 * 削除フラグが立っているかを確認するヘルパー関数
 * @param {*} event schedulerのイベントオブジェクト
 * @returns
 */
const isSoftDeleted = event => {
  return event.innerDelete;
};

/**
 * コピーフラグと削除フラグを適用
 *
 * コピーフラグが立っているレコードをコピーし、削除フラグが立っているレコードを削除します。
 * @private
 * @param {*} store
 */
const applyReplaceAndDeleteAsync = async store => {
  // 置換フラグが立っているレコードをコピー
  const targetReplaceEvents = store.query(event => event.innerReplace, true);
  for (const event of targetReplaceEvents) {
    await copyEventAsync(event, store);
  }

  // 置換フラグ・削除フラグが立っているレコードを削除
  store
    .query(event => event.innerDelete, true)
    .forEach(event => {
      // 乗船期間IDが変更されているレコードは元に戻して削除
      const previous_enterprise_manning_vessel_crew_id = event.getUnmodified(
        ENTERPRISE_MANNING_VESSEL_CREW_ID
      );
      if (previous_enterprise_manning_vessel_crew_id) {
        setEnterpriseManningVesselCrewId(
          event,
          previous_enterprise_manning_vessel_crew_id
        );
      }
      // 内部用削除フラグが立っているレコードを削除
      store.remove(event);
    });
};

/**
 * イベントをコピーして、リソースに割り当てるヘルパー関数
 * @param {*} event schedulerのイベントオブジェクト
 * @param {*} store イベントストア
 * @returns {Promise} コピーされたイベントオブジェクト
 */
const copyEventAsync = async (event, store) => {
  const copyEvent = event.copy(
    { innerReplace: false, innerDelete: false, version: null },
    true
  );

  await store.addAsync(copyEvent);
  store.assignEventToResource(copyEvent, event.resource);

  return copyEvent;
};

/**
 * スケジュールステータスがSCHEDULEDであるかを確認する
 * @param {*} event schedulerのイベントオブジェクト
 * @returns
 */
const isScheduled = event => {
  return event.scheduling_status === C.SCHEDULING_STATUS.SCHEDULED;
};

// 開始日で昇順ソートするヘルパー関数
const sortByStartDate = (a, b) => {
  return a.startDate.getTime() - b.startDate.getTime();
};

// 隣接判定を行うヘルパー関数
const isAdjacent = (event1, event2) => {
  return (
    event1.endDate.getTime() === event2.startDate.getTime() ||
    event1.startDate.getTime() === event2.endDate.getTime()
  );
};

// 重なりをチェックするヘルパー関数
const isOverlapping = (event1, event2) => {
  return (
    event1.startDate.getTime() < event2.endDate.getTime() &&
    event1.endDate.getTime() > event2.startDate.getTime()
  );
};

/**
 * 左側の重なりをチェックするヘルパー関数
 * event1の左側がevent2の右側に重なっているかを確認する
 * @param {*} event1
 * @param {*} event2
 * @returns
 * */
const isOverlappingLeft = (event1, event2) => {
  return (
    event1.startDate.getTime() > event2.startDate.getTime() &&
    event1.startDate.getTime() < event2.endDate.getTime()
  );
};

/**
 * 右側の重なりをチェックするヘルパー関数
 * event1の右側がevent2の左側に重なっているかを確認する
 * @param {*} event1
 * @param {*} event2
 * @returns
 * */
const isOverlappingRight = (event1, event2) => {
  return (
    event1.startDate.getTime() < event2.startDate.getTime() &&
    event1.endDate.getTime() > event2.startDate.getTime()
  );
};

// 重なりまたは隣接をチェックするヘルパー関数
const isOverlappingOrAdjacent = (event1, event2) => {
  return isOverlapping(event1, event2) || isAdjacent(event1, event2);
};

/**
 * 完全に包含されているかをチェックするヘルパー関数
 *
 * event1がevent2の期間内に完全に収まっているかを確認する
 * @param {*} event1
 * @param {*} event2
 * @returns
 */
const isContained = (event1, event2) => {
  return (
    event1.startDate.getTime() >= event2.startDate.getTime() &&
    event1.endDate.getTime() <= event2.endDate.getTime()
  );
};

export default {
  genPhantomGroupKey,
  setPhantomGroupKey,
  setEnterpriseManningVesselCrewId,
  changeEnterpriseManningVesselCrewId,
  softDelete,
  softReplace,
  isSoftDeleted,
  applyReplaceAndDeleteAsync,
  copyEventAsync,
  isScheduled,
  sortByStartDate,
  isAdjacent,
  isOverlapping,
  isOverlappingLeft,
  isOverlappingRight,
  isOverlappingOrAdjacent,
  isContained,
};
